import Talk from 'talkjs'
import { Session } from '@talkjs/react'
import {
  ChatContextType,
  ChatProviderProps,
  ChatRole,
  CreateNewConversationProps,
} from './ChatContext.types'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { InboxDrawer } from '../components/InboxDrawer/InboxDrawer.component'
import { useChatStore } from '../store'
import { usePatientHasNearbyAppointments } from '../queries/Chat.queries'
import { ChatService } from '../services/chat.services'

export const ChatContext = createContext<ChatContextType | null>(null)

export const ChatProvider = ({ children }: ChatProviderProps) => {
  const { user, clinic } = useAuthContext()
  const sessionRef = useRef<Talk.Session | null>(null)
  const [conversation, setConversation] =
    useState<Talk.ConversationBuilder | null>(null)
  const [updateValueChatStore, initChatProvider, resetStore] = useChatStore(
    state => [state.updateValue, state.initChatProvider, state.resetStore],
  )

  const { data: hasNearbyAppointments } = usePatientHasNearbyAppointments({
    enabled: clinic?.flag?.HabilitarChatPacienteProfissional,
  })

  const patientTalkUser = useCallback(
    (data?: CreateNewConversationProps['patient']) =>
      new Talk.User({
        id: user?.patientId,
        name: user?.name,
        email: user?.email,
        role: ChatRole.USER,
        photoUrl: user?.photo || null,
        locale: 'pt-BR',
        ...(data && { ...data }),
      }),
    [user],
  )

  const createProfessionalTalkUser = useCallback(
    (professional: CreateNewConversationProps['professional']) =>
      new Talk.User(professional),
    [],
  )

  useEffect(() => {
    if (
      user?.id &&
      clinic?.flag?.HabilitarChatPacienteProfissional &&
      hasNearbyAppointments
    ) {
      updateValueChatStore('initChatProvider', true)
    } else {
      updateValueChatStore('initChatProvider', false)
    }
  }, [
    clinic?.flag?.HabilitarChatPacienteProfissional,
    user?.id,
    hasNearbyAppointments,
    updateValueChatStore,
  ])

  const createNewSession = useCallback(() => {
    const newSession = new Talk.Session({
      appId: import.meta.env.VITE_APP_TALKJS_APP_ID,
      me: patientTalkUser(),
      tokenFetcher: ChatService.getChatToken,
    })

    sessionRef.current = newSession
    updateValueChatStore('initChatProvider', true)
  }, [patientTalkUser, updateValueChatStore])

  const createNewConversation = useCallback(
    (data: CreateNewConversationProps) => {
      if (!sessionRef.current?.isAlive) {
        createNewSession()
      }

      const professionalTalkUser = createProfessionalTalkUser(data.professional)

      const conversationId = Talk.oneOnOneId(
        patientTalkUser(data?.patient),
        professionalTalkUser,
      )

      const conversation =
        sessionRef.current?.getOrCreateConversation(conversationId)

      conversation.setParticipant(patientTalkUser())
      conversation.setParticipant(professionalTalkUser)

      if (data?.patient?.welcomeMessage) {
        conversation?.sendMessage(data?.patient?.welcomeMessage)
      }

      if (conversation) {
        setConversation(conversation)
        updateValueChatStore('openInboxDrawer', true)
      }
    },
    [
      createProfessionalTalkUser,
      patientTalkUser,
      createNewSession,
      updateValueChatStore,
    ],
  )

  const resetConversationSelected = useCallback(() => {
    setConversation(null)
  }, [])

  const destroyChatSession = useCallback(() => {
    if (sessionRef.current?.isAlive && initChatProvider) {
      sessionRef.current?.destroy()
      sessionRef.current = null
      resetConversationSelected()
      resetStore()
    }
  }, [sessionRef, initChatProvider, resetConversationSelected, resetStore])

  const chatContextValue = useMemo(() => {
    return {
      createNewConversation,
      conversation,
      destroyChatSession,
      resetConversationSelected,
    }
  }, [
    createNewConversation,
    conversation,
    destroyChatSession,
    resetConversationSelected,
  ])

  return (
    <ChatContext.Provider value={chatContextValue}>
      {children}
      {initChatProvider && (
        <Session
          appId={import.meta.env.VITE_APP_TALKJS_APP_ID}
          syncUser={patientTalkUser}
          sessionRef={sessionRef}
          tokenFetcher={ChatService.getChatToken}
        >
          <InboxDrawer />
        </Session>
      )}
    </ChatContext.Provider>
  )
}

export const useChatContext = (): ChatContextType => {
  const context = useContext(ChatContext)

  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider')
  }

  return context
}
